import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@twilio-paste/core/box';
import { Text } from '@twilio-paste/core/text';
import { isAxiosError } from 'axios';
import { pickBy } from 'lodash';

import { SearchCustomerResult } from './SearchCustomerResult';
import { SearchCustomersQueryFilters } from './SearchCustomersQueryFilters';
import { QueryHook } from './types';

interface PanelConfig {
  title: string;
  filters?: { label: string; name: string; optional: boolean }[];
  queryKeys?: string[];
}

type SearchCustomersPanelProps = {
  config: PanelConfig;
  useQueryHook: QueryHook;
};

export function SearchCustomersPanel({ config: {
  title,
  filters = [],
  queryKeys = [],
},
  useQueryHook }: SearchCustomersPanelProps) {
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  const hasAtLeastOne = useMemo(() => queryKeys.some((key) => inputs[key] && inputs[key].trim() !== ''), [inputs, queryKeys]);

  const [params, setParams] = useState<Record<string, string>>({});

  const {
    data,
    error,
    isFetching,
    refetch,
    isFetched,
  } = useQueryHook(params);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInputs({ ...inputs, [event.target.name]: event.target.value });
    setLocalError(null);
  }, [inputs]);

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    if (!hasAtLeastOne) {
      setLocalError('At least one filter must be provided.');
      return;
    }
    setParams(pickBy(inputs, Boolean));
  }, [hasAtLeastOne, inputs]);

  useEffect(() => {
    if (Object.keys(params).length > 0) {
      refetch();
    }
  }, [params, refetch]);

  const responseBody = useMemo(() =>
    isFetched && data
      ? JSON.stringify(data.data, null, 2)
      : isAxiosError(error)
        ? JSON.stringify(error.response?.data, null, 2)
        : ''
    , [isFetched, data, error]);

  const httpStatus = useMemo(() =>
    data
      ? data.status
      : error && isAxiosError(error) && error.response
        ? error.response.status
        : null
    , [data, error]);

  return (
    <Box
      borderStyle="solid"
      borderWidth="borderWidth10"
      borderColor="colorBorderWeak"
      borderRadius="borderRadius20"
      padding="space60"
      backgroundColor="colorBackgroundBody"
      minWidth="320px"
      minHeight="700px"
      width="100%"
      flex="1"
    >
      <Text as="h3" fontWeight="fontWeightBold" fontSize="fontSize30" marginBottom="space40" display="block">{title}</Text>
      <SearchCustomersQueryFilters
        filters={filters}
        inputs={inputs}
        isFetching={isFetching}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
      {localError && (
        <Text as="div" color="colorTextError" marginBottom="space40">
          {localError}
        </Text>
      )}
      <SearchCustomerResult
        responseBody={responseBody}
        isFetched={isFetched}
        data={data}
        httpStatus={httpStatus}
      />
    </Box>
  );
};
